import { useState } from 'react';
import { connect, ConnectedProps, useDispatch } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { maxBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import routes from '@root/routes';
import ExclusiveLabel from '@components/shared/ExclusiveLabel';
import CustomIcon from '@components/shared/CustomIcon';
import Emdash from '@components/shared/Emdash';
import { BuildingListing, StoreState, TourbookSummary } from '@root/types';
import actions from '@store/actions/buildingPage';
import TourbookIconButton from '@components/shared/TourbookIconButton';
import { TourbookOverlay } from '@components/shared';
import useToggle from '@shared/useToggle';
import { tourbookCreateOnly } from '@store/selectors';
import FlexibleSizeArea from '@components/shared/FlexibleSizeArea';
import SpaceConditionCell from './SpaceConditionCell';
import s from '../AvailableSpaces.module.less';
import FloorPlanModal from '../FloorPlanModal';
import AvailabilityCell from './AvailabilityCell';
import LeaseTypeCell from './LeaseTypeCell';

const mapState = (state: StoreState) => ({
  isTourbookCreateOnly: tourbookCreateOnly(state),
});

const mapDispatch = {};

const connector = connect(mapState, mapDispatch);
type ReduxProps = ConnectedProps<typeof connector>;
type Props = {
  listing: BuildingListing;
  buildingSlug: string;
  onClick: () => void;
  isInTourbook: boolean;
  tourbooks: TourbookSummary[];
  isUserLoggedIn: boolean;
};

const TableRow = ({
  listing,
  buildingSlug,
  onClick,
  isInTourbook,
  tourbooks,
  isTourbookCreateOnly,
  isUserLoggedIn,
}: Props & ReduxProps) => {
  const { t } = useTranslation('building');
  const {
    value: tourbookOverlayIsOpen,
    setTrue: openTourbookOverlay,
    setFalse: closeTourbookOverlay,
  } = useToggle();
  const [showFloorPlan, setShowFloorPlan] = useState(false);
  const dispatch = useDispatch();

  const showPricingInfo = listing.baseRents && listing.baseRents.length > 0 && listing.area;
  const baseRent = showPricingInfo ? maxBy(listing.baseRents, 'month') : null;

  const openFloorPlan = e => {
    if (listing.floorPlan) {
      e.stopPropagation();
      e.preventDefault();
      dispatch(actions.buildingSpacesfloorplanClicked());
      setShowFloorPlan(true);
    }
  };

  return (
    <>
      <NavLink
        to={routes.listing(listing.id, buildingSlug)}
        className={s.row}
        onClick={onClick}
        data-testid="listing-row"
      >
        <div className={s.cell}>
          <div className={s.suite}>
            <div className={s.suiteImg}>
              {listing.isCurrentlyExclusive && <ExclusiveLabel size="small" />}
              <img
                className={s.listingImg}
                src={listing.previewPhoto.smallPath}
                alt={listing.previewPhoto.description}
              />
            </div>
            <div className={s.suiteImgOverlayWrapper}>
              <div className={s.suiteImgOverlay}>
                <div className={s.actionButtons}>
                  <TourbookIconButton
                    isInTourbook={isInTourbook}
                    setTourbookDialogIsOpen={openTourbookOverlay}
                  />
                </div>
              </div>
            </div>
            <div className={s.suiteInfo}>
              <div>{listing.floorName}</div>
              <div>{listing.suiteName}</div>
            </div>
          </div>
        </div>
        <div className={s.cell}>
          <div>
            {t('units:area', { area: listing.area })}
            <FlexibleSizeArea minMaxArea={listing.minMaxArea} />
          </div>
        </div>
        <div className={s.cell}>
          <AvailabilityCell
            availableToday={listing.availableToday}
            availabilityDate={listing.availabilityDate}
          />
        </div>
        <div className={cn(s.desktopOnly, s.cell)}>
          <SpaceConditionCell condition={listing.spaceCondition} />
        </div>
        {isUserLoggedIn && (
          <div className={s.cell}>
            <LeaseTypeCell leaseType={listing.leaseType} />
          </div>
        )}
        <div className={cn(s.desktopOnly, s.cell)}>
          {t(`availableSpaces.${listing.furnished ? 'furnished' : 'notFurnished'}`)}
        </div>
        <div className={cn(s.desktopOnly, s.cell)}>
          <div data-testid="priceInformation">
            {showPricingInfo ? (
              t('units:pricePerAreaPerPeriod', {
                pricePerAreaPerPeriod: baseRent,
              })
            ) : (
              <Emdash muted />
            )}
          </div>
        </div>
        <div
          className={cn(s.cell, s.floorPlanButton, {
            [s.floorPlanSeparator]: listing.floorPlan,
          })}
          onClick={openFloorPlan}
          role="button"
        >
          {listing.floorPlan ? (
            <div className={s.floorPlanButtonContainer}>
              <button
                className={s.floorPlanIconButton}
                type="button"
                title={t('availableSpaces.headers.floorPlan')}
              >
                <CustomIcon className={s.floorPlanIcon} type="floor-plan" />
              </button>
            </div>
          ) : (
            <Emdash muted />
          )}
          {showFloorPlan ? (
            <FloorPlanModal
              floorPlanUrl={listing.floorPlan as string}
              onClose={e => {
                e.stopPropagation();
                e.preventDefault();
                setShowFloorPlan(false);
              }}
            />
          ) : null}
        </div>
      </NavLink>
      {tourbookOverlayIsOpen && (
        <TourbookOverlay
          isOpen={tourbookOverlayIsOpen}
          close={closeTourbookOverlay}
          listing={listing}
          createOnly={isTourbookCreateOnly}
          tourbooks={tourbooks}
          className={s.tourbookOverlay}
        />
      )}
      <div className={s.rowSpacer} />
    </>
  );
};

export default connector(TableRow);
